import { t } from '../i18n/dictionaries'
import {
  applyDocumentLocale,
  resolveDefaultLocale,
} from './locale'
import { renderChrome } from './render-chrome'
import { applyTheme, watchSystemTheme } from './theme'
import type { PageId } from './shell'
import { SUPPORTED_LOCALES, type Locale } from '../site'
import {
  getFollowSystem,
  setFollowSystem,
  setLocale,
} from './locale'
import { getThemeMode, setThemeMode } from './theme'
import { initPrivacyConsent } from './privacy-consent'

export function bootToolPage(pageId: PageId): Locale {
  applyTheme()
  watchSystemTheme()
  const locale = resolveDefaultLocale()
  applyDocumentLocale(locale)
  renderChrome(locale, pageId)
  hydrateI18n(locale)
  decorateToolPage(pageId, locale)
  markActiveNav(pageId)
  wireChrome(locale)
  markMobileTab(pageId)
  initPrivacyConsent(locale)
  document.documentElement.classList.remove('boot-pending')
  return locale
}

function decorateToolPage(pageId: PageId, locale: Locale): void {
  if (pageId === 'home' || pageId === 'qa' || pageId === 'article') return
  const hero = document.querySelector<HTMLElement>('.page-hero')
  if (hero && !hero.querySelector('.page-hero__readout')) {
    hero.insertAdjacentHTML(
      'beforeend',
      `<aside class="page-hero__readout" aria-label="${t(locale, 'tool_eyebrow')}">
        <div class="page-hero__readout-label">${t(locale, 'privacy_note')}</div>
        <div class="page-hero__readout-value">READY</div>
        <div class="page-hero__readout-meta">
          <span class="tag tag--accent">LOCAL</span>
          <span class="tag">${pageId.toUpperCase()}</span>
        </div>
      </aside>`,
    )
  }

  document.querySelectorAll<HTMLElement>('.surface-card').forEach((card, index) => {
    card.style.setProperty('--stagger', `${Math.min(index * 55, 220)}ms`)
    card.classList.add('instrument-surface')
  })
}

function hydrateI18n(locale: Locale): void {
  document.querySelectorAll<HTMLElement>('[data-i18n]').forEach((node) => {
    const key = node.dataset.i18n
    if (!key) return
    node.textContent = t(locale, key)
  })
}

function markActiveNav(pageId: PageId): void {
  document.querySelectorAll<HTMLElement>('[data-nav]').forEach((node) => {
    node.classList.toggle('is-active', node.dataset.nav === pageId)
  })
}

function markMobileTab(pageId: PageId): void {
  const groups: Record<string, PageId[]> = {
    '/index.html': ['home'],
    '/solid-color.html': ['solid-color', 'gradient', 'grid', 'uniformity', 'anti-burn-in'],
    '/refresh-rate.html': ['refresh-rate', 'motion'],
    '/touch.html': ['touch'],
    '/qa/index.html': ['settings', 'report', 'media-lab', 'safe-area', 'inspect', 'qa', 'article'],
  }
  document.querySelectorAll('.glass-tab').forEach((node) => {
    const href = (node as HTMLAnchorElement).getAttribute('href') || ''
    const ids = Object.entries(groups).find(([key]) => href.endsWith(key))?.[1] ?? []
    node.classList.toggle('is-active', ids.includes(pageId))
  })
}

function wireChrome(locale: Locale): void {
  const langBtn = document.querySelector<HTMLButtonElement>('[data-action="lang"]')
  const langMenu = document.querySelector<HTMLElement>('[data-menu="lang"]')
  const themeBtn = document.querySelector<HTMLButtonElement>('[data-action="theme"]')
  const themeMenu = document.querySelector<HTMLElement>('[data-menu="theme"]')

  langBtn?.addEventListener('click', (event) => {
    event.stopPropagation()
    themeMenu?.setAttribute('hidden', '')
    langMenu?.toggleAttribute('hidden')
  })

  themeBtn?.addEventListener('click', (event) => {
    event.stopPropagation()
    langMenu?.setAttribute('hidden', '')
    themeMenu?.toggleAttribute('hidden')
  })

  document.addEventListener('click', () => {
    langMenu?.setAttribute('hidden', '')
    themeMenu?.setAttribute('hidden', '')
  })

  langMenu?.querySelectorAll<HTMLButtonElement>('[data-locale]').forEach((btn) => {
    const value = btn.dataset.locale
    if (value === locale) btn.classList.add('is-active')
    if (value === 'follow') {
      if (getFollowSystem()) btn.classList.add('is-active')
      btn.addEventListener('click', () => {
        setFollowSystem(true)
        location.reload()
      })
      return
    }
    btn.addEventListener('click', () => {
      if (!value || !(SUPPORTED_LOCALES as readonly string[]).includes(value)) return
      setLocale(value as Locale)
      location.reload()
    })
  })

  themeMenu?.querySelectorAll<HTMLButtonElement>('[data-theme-mode]').forEach((btn) => {
    const mode = btn.dataset.themeMode
    if (mode === getThemeMode()) btn.classList.add('is-active')
    btn.addEventListener('click', () => {
      if (mode === 'light' || mode === 'dark' || mode === 'system') {
        setThemeMode(mode)
        location.reload()
      }
    })
  })
}
